import React, { useEffect, useRef } from "react";
import {
  ArrowRightOutlined,
  EnterOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  ApartmentOutlined,
  MedicineBoxOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SolutionOutlined,
  ScheduleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { uri_img } from "../../assets/utils/http-request";
import lib from "../../assets/utils/lib";
import { getFacilityPlanType, getFacilityTypeName } from "../../features/facilities/facilityUtils";

const TARGET_ICONS = {
  clients: UserOutlined,
  enrollees: TeamOutlined,
  facilities: BankOutlined,
  corporations: ApartmentOutlined,
  constableItems: MedicineBoxOutlined,
  pa: FileProtectOutlined,
  claims: FileTextOutlined,
  checkins: CalendarOutlined,
  onboarding: SolutionOutlined,
  appointments: ScheduleOutlined,
};

const STATUS_CLASS = {
  Approved: "global-search__card-badge--success",
  Declined: "global-search__card-badge--danger",
  Queried: "global-search__card-badge--warn",
  Pending: "global-search__card-badge--warn",
  Active: "global-search__card-badge--success",
  Inactive: "global-search__card-badge--neutral",
  Processing: "global-search__card-badge--info",
  Claim: "global-search__card-badge--info",
  PA: "global-search__card-badge--info",
};

/**
 * @param {string} target
 * @param {object} record
 */
export function getGlobalSearchResultPreview(target, record) {
  if (!record) return { title: "Unknown", desc: "", badge: null, badgeClass: "" };

  switch (target) {
    case "clients":
      return {
        title: record.name || record.policy_no || "Client",
        desc: [record.policy_no, record.email, record.insurance_package]
          .filter(Boolean)
          .join(" · "),
        badge: record.status === 1 ? "Active" : "Inactive",
        badgeClass: STATUS_CLASS[record.status === 1 ? "Active" : "Inactive"],
        avatarUrl: null,
      };
    case "enrollees": {
      const name = [record.first_name, record.middle_name, record.surname]
        .filter(Boolean)
        .join(" ");
      return {
        title: name || record.insurance_no || "Enrollee",
        desc: [record.insurance_no, record.policy_no, record.phone]
          .filter(Boolean)
          .join(" · "),
        badge: record.status === 1 ? "Active" : record.role || "Enrollee",
        badgeClass:
          STATUS_CLASS[record.status === 1 ? "Active" : ""] ||
          "global-search__card-badge--neutral",
        avatarUrl: record.pic_name ? `${uri_img}${record.pic_name}` : null,
      };
    }
    case "facilities": {
      const facilityType = getFacilityTypeName(record);
      const planType = getFacilityPlanType(record);
      return {
        title: record.facility_name || "Facility",
        desc: [record.facility_city, record.facility_state, facilityType, planType]
          .filter(Boolean)
          .join(" · "),
        badge: record.facility_status === 1 ? "Active" : "Inactive",
        badgeClass: STATUS_CLASS[record.facility_status === 1 ? "Active" : "Inactive"],
      };
    }
    case "corporations":
      return {
        title: record.org_name || "Organization",
        desc: [record.org_social_no, record.org_email, record.org_city]
          .filter(Boolean)
          .join(" · "),
        badge: "Corporation",
        badgeClass: "global-search__card-badge--neutral",
      };
    case "constableItems":
      return {
        title: record.item_name || record.item_code || "Item",
        desc: [record.item_code, record.category_name].filter(Boolean).join(" · "),
        badge: record.category_name,
        badgeClass: "global-search__card-badge--neutral",
      };
    case "pa":
      return {
        title:
          `${record.first_name || ""} ${record.surname || ""}`.trim() ||
          record.checkin_id,
        desc: [record.checkin_id, record.facility_name, record.insurance_no]
          .filter(Boolean)
          .join(" · "),
        badge: "PA",
        badgeClass: STATUS_CLASS.PA,
      };
    case "claims":
      return {
        title:
          record.claims_id != null
            ? `Medical Claim #CL-${record.claims_id}`
            : `${record.first_name || ""} ${record.surname || ""}`.trim(),
        desc: [
          record.checkin_code || record.checkin_id,
          record.insurance_no,
        ]
          .filter(Boolean)
          .join(" · "),
        badge: "Processing",
        badgeClass: STATUS_CLASS.Processing,
      };
    case "checkins": {
      const c = record.consumer || {};
      const status = record.aggregated_status || "Pending";
      return {
        title:
          `${c.first_name || ""} ${c.surname || ""}`.trim() || record.checkin_id,
        desc: [
          record.facility_name,
          record.plan_name,
          record.checkin_id,
        ]
          .filter(Boolean)
          .join(" · "),
        badge: status,
        badgeClass: STATUS_CLASS[status] || "global-search__card-badge--neutral",
      };
    }
    case "onboarding":
      return {
        title: record.name || "Onboarding lead",
        desc: [record.phone, record.email, record.social_no].filter(Boolean).join(" · "),
        badge: lib.unifiedSearchIsActiveStatus(record.status) ? "Active" : "Lead",
        badgeClass: STATUS_CLASS[lib.unifiedSearchIsActiveStatus(record.status) ? "Active" : "Inactive"],
      };
    case "appointments":
      return {
        title:
          record.patient_name ||
          [record.first_name, record.surname].filter(Boolean).join(" ") ||
          "Appointment",
        desc: [record.facility_name, record.scheduled_start, record.insurance_no]
          .filter(Boolean)
          .join(" · "),
        badge: record.status || "scheduled",
        badgeClass: "global-search__card-badge--info",
      };
    default:
      return {
        title: record.name || record.id || "Result",
        desc: "",
        badge: null,
        badgeClass: "",
      };
  }
}

function CardAvatar({ preview, Icon }) {
  if (preview.avatarUrl) {
    return (
      <div className="global-search__card-avatar">
        <img src={preview.avatarUrl} alt="" />
      </div>
    );
  }
  return (
    <div className="global-search__card-avatar">
      <Icon />
    </div>
  );
}

export function GlobalSearchResultList({
  listTarget,
  results,
  activeIndex,
  onSelect,
  onHover,
}) {
  const resource =
    lib.unifiedSearchTargetToResource(listTarget) || listTarget;
  const Icon = TARGET_ICONS[listTarget] || SearchOutlined;
  const activeRef = useRef(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex]);

  return (
    <div
      className="global-search__results-grid"
      role="listbox"
      aria-label="Search results"
    >
      {results.map((record, index) => {
        const preview = getGlobalSearchResultPreview(listTarget, record);
        const isActive = index === activeIndex;

        return (
          <button
            key={lib.unifiedSearchRowKey(resource, record, index)}
            type="button"
            role="option"
            aria-selected={isActive}
            ref={isActive ? activeRef : null}
            className={`global-search__card${isActive ? " global-search__card--active" : ""}`}
            onClick={() => onSelect(record, listTarget)}
            onMouseEnter={() => onHover?.(index)}
          >
            <CardAvatar preview={preview} Icon={Icon} />
            <span className="global-search__card-body">
              <span className="global-search__card-title-row">
                <span className="global-search__card-title">{preview.title}</span>
                {preview.badge ? (
                  <span
                    className={`global-search__card-badge ${preview.badgeClass || "global-search__card-badge--neutral"}`}
                  >
                    {preview.badge}
                  </span>
                ) : null}
              </span>
              {preview.desc ? (
                <span className="global-search__card-desc">{preview.desc}</span>
              ) : null}
            </span>
            <span className="global-search__card-trail">
              <span className="global-search__card-enter">
                <EnterOutlined />
                <span>PRESS ENTER</span>
              </span>
              <ArrowRightOutlined className="global-search__card-arrow" />
            </span>
          </button>
        );
      })}
    </div>
  );
}
